'use client';

import type { ComponentPropsWithoutRef, Ref } from 'react';

import classnames from 'classnames';

import type { BaseComponentProps } from '../../../base-component.types';
import { Option } from '../../layout/Option';
import { QuestionStatement } from '../../layout/QuestionStatement';
import type { MultipleChoiceQuestionAnswerType } from '../MultipleChoiceQuestionAnswer.types';
import { AnswerExplanation } from './AnswerExplanation';

import styles from './Answer.module.scss';

type AnswerProps = ComponentPropsWithoutRef<'div'> &
  BaseComponentProps & {
    question: MultipleChoiceQuestionAnswerType;
    showReason?: boolean;
    showExplanation?: boolean;
  };

/**
 * How the option was answered: the one that was right, the one that was picked instead,
 * or nothing picked at all — in which case the right one is called out as skipped.
 */
function optionVariant(
  question: MultipleChoiceQuestionAnswerType,
  option: number,
) {
  if (option !== question.correctOptionIndex) {
    return option === question.selectedOptionIndex ? 'incorrect' : 'neutral';
  }

  return question.selectedOptionIndex === 0 ? 'skipped' : 'correct';
}

export function Answer({
  style,
  className,
  question,
  showReason = false,
  showExplanation = false,
  ref,
  ...rest
}: AnswerProps & { ref?: Ref<HTMLDivElement> }) {
  const showsProse = showReason || showExplanation;

  return (
    <div
      ref={ref}
      style={style}
      className={classnames(styles['main'], className)}
      {...rest}
    >
      {!showsProse && <QuestionStatement question={question} />}
      {showReason && question.reason ? (
        <AnswerExplanation
          title="Retroalimentación"
          variant="reason"
          html={question.reason}
          tags={question.tags}
        />
      ) : showExplanation ? (
        <AnswerExplanation
          title="Análisis de tu respuesta"
          variant="explanation"
          html={question.explanation}
        />
      ) : (
        <div className={styles['options']}>
          {question.options.map((option, index) => (
            <Option
              key={index}
              id={index + 1}
              text={option}
              appearance="plain"
              state={optionVariant(question, index + 1)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
